import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES, buildTelehealthSessionPath, buildProviderSessionNotesPath } from "@/routes";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { 
  Calendar as CalendarIcon, 
  Clock, 
  Video, 
  User,
  ChevronLeft,
  ChevronRight,
  Settings,
  Check,
  X,
  Plus,
  FileText,
  Search
} from "lucide-react";
import { ProviderSidebar } from "@/components/layout/ProviderSidebar";
import { ProviderHeader } from "@/components/layout/ProviderHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths, isToday, isBefore, startOfDay } from "date-fns";
import moment from "moment";
import { displayTime, displayDate } from "@/lib/timeUtils";
import { NewSessionDialog } from "@/components/dashboard/NewSessionDialog";
import { ScheduleItemDetailsDialog, AppointmentData } from "@/components/dashboard/ScheduleItemDetailsDialog";

const statusConfig = {
  scheduled: { label: "Scheduled", color: "bg-blue-100 text-blue-700" },
  waiting: { label: "Waiting", color: "bg-amber-100 text-amber-700" },
  in_progress: { label: "In Progress", color: "bg-emerald-100 text-emerald-700" },
  completed: { label: "Completed", color: "bg-muted text-muted-foreground" },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-700" },
};

const visitTypeConfig = {
  general: { label: "General Consultation", icon: Video },
  followup: { label: "Follow-up", icon: Video },
  urgent: { label: "Urgent Care", icon: Video },
  mental_health: { label: "Mental Health", icon: Video },
};

const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const dayNamesFull = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export default function ProviderSchedule() {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [availabilityOpen, setAvailabilityOpen] = useState(false);
  const [newSessionOpen, setNewSessionOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [listTab, setListTab] = useState("upcoming");
  const [pastSearch, setPastSearch] = useState("");
  const [pastDateFilter, setPastDateFilter] = useState<Date | undefined>(undefined);
  const [selectedAppointment, setSelectedAppointment] = useState<AppointmentData | null>(null);

  // Fetch provider profile for availability settings
  const { data: providerProfile } = useQuery({
    queryKey: ["provider-profile", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("providers")
        .select("*")
        .eq("user_id", user?.id)
        .single();
      
      if (error && error.code !== "PGRST116") throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  // Fetch provider availability slots from provider_availability table
  const { data: availabilitySlots } = useQuery({
    queryKey: ["provider-availability", providerProfile?.id],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("provider_availability")
        .select("*")
        .eq("provider_id", providerProfile!.id)
        .order("day", { ascending: true });
      if (error) throw error;
      return data as { id: string; provider_id: string; day: number; start_hour: number; end_hour: number }[];
    },
    enabled: !!providerProfile?.id,
  });

  // Fetch appointments
  const { data: appointments, isLoading } = useQuery({
    queryKey: ["provider-appointments", user?.id, format(currentMonth, "yyyy-MM")],
    queryFn: async () => {
      const start = startOfMonth(currentMonth);
      const end = endOfMonth(currentMonth);
      
      const { data: sessions, error } = await supabase
        .from("telehealth_sessions")
        .select("*")
        .eq("provider_id", user!.id)
        .gte("scheduled_date", format(start, "yyyy-MM-dd"))
        .lte("scheduled_date", format(end, "yyyy-MM-dd"))
        .order("scheduled_date", { ascending: true })
        .order("scheduled_time", { ascending: true });

      if (error) throw error;

      // Get member names - try intake forms first, fall back to profiles
      const memberIds = [...new Set(sessions?.map(s => s.member_id) || [])];
      
      if (memberIds.length === 0) return [];

      const [{ data: intakeForms }, { data: profiles }] = await Promise.all([
        supabase
          .from("patients")
          .select("user_id, first_name, last_name")
          .in("user_id", memberIds),
        supabase
          .from("profiles")
          .select("id, display_name")
          .in("id", memberIds),
      ]);

      return sessions?.map(session => {
        const intake = intakeForms?.find(i => i.user_id === session.member_id);
        const profile = profiles?.find(p => p.id === session.member_id);
        const memberName = intake
          ? `${intake.first_name} ${intake.last_name}`
          : profile?.display_name || "Unknown Patient";
        return { ...session, member_name: memberName };
      }) || [];
    },
    enabled: !!user?.id,
    refetchInterval: 15000, // Fallback polling every 15s
  });

  // Realtime subscription for instant updates
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel("provider-schedule-realtime")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "telehealth_sessions",
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["provider-appointments"] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, queryClient]);

  // Update accepting patients on provider profile
  const updateAcceptingPatients = useMutation({
    mutationFn: async (is_accepting_patients: boolean) => {
      if (!providerProfile) {
        const { error } = await supabase
          .from("providers")
          .insert({
            user_id: user?.id,
            display_name: "Provider",
            specialty: "General Care",
            is_accepting_patients,
          });
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("providers")
          .update({ is_accepting_patients })
          .eq("id", providerProfile.id);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["provider-profile"] });
      toast({ title: "Setting updated", description: "Your patient acceptance setting has been saved." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update setting.", variant: "destructive" });
    },
  });

  // Upsert a day's availability (toggle on with defaults, or update hours)
  const upsertAvailability = useMutation({
    mutationFn: async (slot: { day: number; start_hour: number; end_hour: number }) => {
      if (!providerProfile) throw new Error("No provider profile");
      const { error } = await (supabase as any)
        .from("provider_availability")
        .upsert(
          { provider_id: providerProfile.id, day: slot.day, start_hour: slot.start_hour, end_hour: slot.end_hour },
          { onConflict: "provider_id,day" }
        );
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["provider-availability"] });
      toast({ title: "Availability updated", description: "Your schedule settings have been saved." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update availability.", variant: "destructive" });
    },
  });

  // Remove a day's availability
  const removeAvailability = useMutation({
    mutationFn: async (day: number) => {
      if (!providerProfile) throw new Error("No provider profile");
      const { error } = await (supabase as any)
        .from("provider_availability")
        .delete()
        .eq("provider_id", providerProfile.id)
        .eq("day", day);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["provider-availability"] });
      toast({ title: "Availability updated", description: "Day removed from schedule." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update availability.", variant: "destructive" });
    },
  });

  // Get appointments for selected date
  const selectedDateAppointments = appointments?.filter(apt => 
    isSameDay(new Date(apt.scheduled_date), selectedDate)
  ) || [];

  // Get appointments count per day for calendar
  const getAppointmentCountForDate = (date: Date) => {
    return appointments?.filter(apt => 
      isSameDay(new Date(apt.scheduled_date), date)
    ).length || 0;
  };

  // Calendar days for current month view
  const calendarDays = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth),
  });

  // Get first day offset (what day of week does month start on)
  const firstDayOffset = startOfMonth(currentMonth).getDay();

  const startSession = useMutation({
    mutationFn: async (sessionId: string) => {
      const roomId = `room-${sessionId}`;
      const { error } = await supabase
        .from("telehealth_sessions")
        .update({ status: "in_progress", started_at: new Date().toISOString(), room_id: roomId })
        .eq("id", sessionId);
      if (error) throw error;
      return sessionId;
    },
    onSuccess: (sessionId) => {
      queryClient.invalidateQueries({ queryKey: ["provider-appointments"] });
      navigate(buildTelehealthSessionPath(sessionId));
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to start session.", variant: "destructive" });
    },
  });

  const activeDays = (availabilitySlots || []).map(s => s.day);

  const handleDayToggle = (dayIndex: number) => {
    if (activeDays.includes(dayIndex)) {
      removeAvailability.mutate(dayIndex);
    } else {
      upsertAvailability.mutate({ day: dayIndex, start_hour: 9, end_hour: 17 });
    }
  };

  const getSlotForDay = (day: number) => availabilitySlots?.find(s => s.day === day);

  const handleOpenDetails = (apt: AppointmentData) => {
    setSelectedAppointment(apt);
    setDetailsDialogOpen(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <ProviderSidebar />
      <div className="ml-64">
        <ProviderHeader />
        <main className="p-6">
          {/* Page Header */}
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="font-display text-3xl font-bold text-foreground flex items-center gap-3">
                <CalendarIcon className="h-8 w-8 text-primary" />
                Schedule
              </h1>
              <p className="mt-1 text-muted-foreground">
                Manage your appointments and availability
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={() => setNewSessionOpen(true)} className="gap-2">
                <Plus className="h-4 w-4" />
                Schedule New Session
              </Button>
              <Dialog open={availabilityOpen} onOpenChange={setAvailabilityOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline">
                    <Settings className="h-4 w-4 mr-2" />
                    Availability Settings
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Availability Settings</DialogTitle>
                    <DialogDescription>
                      Configure your working hours and available days
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-6 py-4">
                    {/* Accepting Patients Toggle */}
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Accepting New Patients</Label>
                        <p className="text-sm text-muted-foreground">
                          Allow members to schedule appointments
                        </p>
                      </div>
                      <Switch
                        checked={providerProfile?.is_accepting_patients ?? true}
                        onCheckedChange={(checked) => 
                          updateAcceptingPatients.mutate(checked)
                        }
                      />
                    </div>

                    {/* Working Days */}
                    <div className="space-y-2">
                      <Label>Working Days</Label>
                      <p className="text-xs text-muted-foreground">Toggle days on/off, then set hours per day</p>
                      <div className="flex flex-wrap gap-2">
                        {dayNames.map((day, index) => {
                          const isActive = activeDays.includes(index);
                          return (
                            <Button
                              key={day}
                              variant={isActive ? "default" : "outline"}
                              size="sm"
                              onClick={() => handleDayToggle(index)}
                              className="w-12"
                            >
                              {day}
                            </Button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Per-day working hours */}
                    {activeDays.length > 0 && (
                      <div className="space-y-3">
                        <Label>Hours Per Day</Label>
                        {activeDays.sort((a, b) => a - b).map((dayIdx) => {
                          const slot = getSlotForDay(dayIdx);
                          return (
                            <div key={dayIdx} className="flex items-center gap-3">
                              <span className="w-10 text-sm font-medium text-muted-foreground">{dayNames[dayIdx]}</span>
                              <Select
                                value={String(slot?.start_hour ?? 9)}
                                onValueChange={(val) =>
                                  upsertAvailability.mutate({ day: dayIdx, start_hour: parseInt(val), end_hour: slot?.end_hour ?? 17 })
                                }
                              >
                                <SelectTrigger className="w-[110px]">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {Array.from({ length: 12 }, (_, i) => i + 6).map((hour) => (
                                    <SelectItem key={hour} value={String(hour)}>
                                      {hour === 12 ? "12:00 PM" : hour > 12 ? `${hour - 12}:00 PM` : `${hour}:00 AM`}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <span className="text-sm text-muted-foreground">to</span>
                              <Select
                                value={String(slot?.end_hour ?? 17)}
                                onValueChange={(val) =>
                                  upsertAvailability.mutate({ day: dayIdx, start_hour: slot?.start_hour ?? 9, end_hour: parseInt(val) })
                                }
                              >
                                <SelectTrigger className="w-[110px]">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {Array.from({ length: 12 }, (_, i) => i + 12).map((hour) => (
                                    <SelectItem key={hour} value={String(hour)}>
                                      {hour === 12 ? "12:00 PM" : hour > 12 ? `${hour - 12}:00 PM` : `${hour}:00 AM`}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Calendar */}
            <Card className="lg:col-span-2">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle>{format(currentMonth, "MMMM yyyy")}</CardTitle>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setCurrentMonth(new Date());
                        setSelectedDate(new Date());
                      }}
                    >
                      Today
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-1">
                  {/* Day headers */}
                  {dayNames.map((day) => (
                    <div
                      key={day}
                      className="p-2 text-center text-sm font-medium text-muted-foreground"
                    >
                      {day}
                    </div>
                  ))}
                  
                  {/* Empty cells for offset */}
                  {Array.from({ length: firstDayOffset }).map((_, i) => (
                    <div key={`empty-${i}`} className="p-2" />
                  ))}
                  
                  {/* Calendar days */}
                  {calendarDays.map((day) => {
                    const appointmentCount = getAppointmentCountForDate(day);
                    const isSelected = isSameDay(day, selectedDate);
                    const isPast = isBefore(day, startOfDay(new Date()));
                    
                    return (
                      <button
                        key={day.toISOString()}
                        onClick={() => setSelectedDate(day)}
                        className={`
                          relative p-2 min-h-16 text-left rounded-lg transition-colors
                          ${isSelected ? "bg-primary text-primary-foreground" : "hover:bg-muted"}
                          ${isToday(day) && !isSelected ? "ring-2 ring-primary ring-offset-2" : ""}
                          ${isPast && !isSelected ? "text-muted-foreground" : ""}
                        `}
                      >
                        <span className="text-sm font-medium">{format(day, "d")}</span>
                        {appointmentCount > 0 && (
                          <div className={`
                            absolute bottom-1 right-1 h-5 min-w-5 px-1 rounded-full text-xs flex items-center justify-center
                            ${isSelected ? "bg-primary-foreground text-primary" : "bg-primary text-primary-foreground"}
                          `}>
                            {appointmentCount}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Selected Day Details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  {format(selectedDate, "EEEE, MMMM d")}
                </CardTitle>
                <CardDescription>
                  {selectedDateAppointments.length} appointment{selectedDateAppointments.length !== 1 ? "s" : ""}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  </div>
                ) : selectedDateAppointments.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <CalendarIcon className="h-10 w-10 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">No appointments scheduled</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {selectedDateAppointments.map((apt) => (
                      <div
                        key={apt.id}
                        className="rounded-lg border p-3 space-y-2 cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={(e) => {
                          // Prevent opening details if clicking on buttons
                          if ((e.target as HTMLElement).closest('button')) return;
                          handleOpenDetails(apt);
                        }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            <Avatar className="h-8 w-8">
                              <AvatarFallback className="bg-primary/10 text-primary text-xs">
                                {apt.member_name.split(" ").map((n: string) => n[0]).join("")}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium text-sm">{apt.member_name}</p>
                              <p className="text-xs text-muted-foreground capitalize">
                                {apt.visit_type.replace("_", " ")}
                              </p>
                            </div>
                          </div>
                          <Badge className={statusConfig[apt.status as keyof typeof statusConfig]?.color || ""}>
                            {statusConfig[apt.status as keyof typeof statusConfig]?.label || apt.status}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5" />
                            {apt.scheduled_date ? displayTime(apt.scheduled_time, apt.scheduled_date) : apt.scheduled_time.slice(0, 5)}
                          </div>
                          <div className="flex items-center gap-1">
                            <Video className="h-3.5 w-3.5" />
                            {apt.duration_minutes} min
                          </div>
                        </div>
                        {!isBefore(startOfDay(selectedDate), startOfDay(new Date())) && (apt.status === "scheduled" || apt.status === "waiting") && (
                          <Button
                            size="sm"
                            className="w-full mt-2"
                            onClick={() => startSession.mutate(apt.id)}
                            disabled={startSession.isPending}
                          >
                            <Video className="h-3.5 w-3.5 mr-1.5" />
                            {apt.status === "waiting" ? "Join Session" : "Start Session"}
                          </Button>
                        )}
                        {!isBefore(startOfDay(selectedDate), startOfDay(new Date())) && apt.status === "in_progress" && (
                          <Button
                            size="sm"
                            variant="success"
                            className="w-full mt-2"
                            onClick={() => navigate(buildTelehealthSessionPath(apt.id))}
                          >
                            <Video className="h-3.5 w-3.5 mr-1.5" />
                            Rejoin Session
                          </Button>
                        )}
                        {isBefore(startOfDay(selectedDate), startOfDay(new Date())) && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-full mt-2"
                            onClick={() => navigate(buildProviderSessionNotesPath(apt.id))}
                          >
                            <FileText className="h-3.5 w-3.5 mr-1.5" />
                            View Notes
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Appointments List with Tabs */}
          <Card className="mt-6">
            <CardHeader className="pb-2">
              <Tabs value={listTab} onValueChange={setListTab}>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Appointments</CardTitle>
                    <CardDescription className="mt-1">Your scheduled sessions this month</CardDescription>
                  </div>
                  <TabsList>
                    <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
                    <TabsTrigger value="past">Past</TabsTrigger>
                  </TabsList>
                </div>
              </Tabs>
            </CardHeader>
            <CardContent>
              <Tabs value={listTab} onValueChange={setListTab}>
                <TabsContent value="upcoming" className="mt-0">
                  {isLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    </div>
                  ) : (() => {
                    const todayStr = format(startOfDay(new Date()), "yyyy-MM-dd");
                    const upcoming = (appointments || [])
                      .filter(apt => apt.scheduled_date >= todayStr && (apt.status === "scheduled" || apt.status === "waiting" || apt.status === "in_progress"))
                      .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date) || a.scheduled_time.localeCompare(b.scheduled_time));
                    return upcoming.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <CalendarIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>No upcoming appointments</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {upcoming.map((apt) => (
                          <div
                            key={apt.id}
                            className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors cursor-pointer"
                            onClick={(e) => {
                              if ((e.target as HTMLElement).closest('button')) return;
                              handleOpenDetails(apt);
                            }}
                          >
                            <div className="flex items-center gap-3">
                              <Avatar className="h-10 w-10">
                                <AvatarFallback className="bg-primary/10 text-primary">
                                  {apt.member_name.split(" ").map((n: string) => n[0]).join("")}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium">{apt.member_name}</p>
                                <p className="text-sm text-muted-foreground capitalize">
                                  {apt.visit_type.replace("_", " ")}
                                  {apt.visit_reason && ` • ${apt.visit_reason}`}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <p className="font-medium">{displayDate(apt.scheduled_date)}</p>
                                <p className="text-sm text-muted-foreground">
                                  {apt.scheduled_date ? displayTime(apt.scheduled_time, apt.scheduled_date) : apt.scheduled_time.slice(0, 5)} • {apt.duration_minutes} min
                                </p>
                              </div>
                              <Badge className={statusConfig[apt.status as keyof typeof statusConfig]?.color || ""}>
                                {statusConfig[apt.status as keyof typeof statusConfig]?.label || apt.status}
                              </Badge>
                              {apt.status === "in_progress" ? (
                                <Button size="sm" variant="success" onClick={() => navigate(buildTelehealthSessionPath(apt.id))}>
                                  <Video className="h-3.5 w-3.5 mr-1.5" />
                                  Rejoin
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant={apt.status === "waiting" ? "success" : "default"}
                                  onClick={() => startSession.mutate(apt.id)}
                                  disabled={startSession.isPending}
                                >
                                  <Video className="h-3.5 w-3.5 mr-1.5" />
                                  {apt.status === "waiting" ? "Join" : "Start"}
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </TabsContent>

                <TabsContent value="past" className="mt-0">
                  {/* Filters */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search by patient name..."
                        value={pastSearch}
                        onChange={(e) => setPastSearch(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className={`gap-2 ${pastDateFilter ? "text-primary" : ""}`}>
                          <CalendarIcon className="h-4 w-4" />
                          {pastDateFilter ? format(pastDateFilter, "MMM d, yyyy") : "Filter by date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="end">
                        <Calendar
                          mode="single"
                          selected={pastDateFilter}
                          onSelect={setPastDateFilter}
                          disabled={(d) => !isBefore(d, startOfDay(new Date()))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    {pastDateFilter && (
                      <Button variant="ghost" size="sm" onClick={() => setPastDateFilter(undefined)}>
                        Clear
                      </Button>
                    )}
                  </div>

                  {isLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    </div>
                  ) : (() => {
                    const todayStr = format(startOfDay(new Date()), "yyyy-MM-dd");
                    let past = (appointments || [])
                      .filter(apt => 
                        apt.status === "completed" || apt.status === "cancelled" || apt.scheduled_date < todayStr
                      )
                      .sort((a, b) => b.scheduled_date.localeCompare(a.scheduled_date) || b.scheduled_time.localeCompare(a.scheduled_time));

                    if (pastSearch.trim()) {
                      const q = pastSearch.toLowerCase();
                      past = past.filter(apt => apt.member_name.toLowerCase().includes(q));
                    }
                    if (pastDateFilter) {
                      const filterStr = format(pastDateFilter, "yyyy-MM-dd");
                      past = past.filter(apt => apt.scheduled_date === filterStr);
                    }

                    return past.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <CalendarIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>No past sessions found</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {past.map((apt) => (
                          <div
                            key={apt.id}
                            className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors cursor-pointer"
                            onClick={(e) => {
                              if ((e.target as HTMLElement).closest('button')) return;
                              handleOpenDetails(apt);
                            }}
                          >
                            <div className="flex items-center gap-3">
                              <Avatar className="h-10 w-10">
                                <AvatarFallback className="bg-primary/10 text-primary">
                                  {apt.member_name.split(" ").map((n: string) => n[0]).join("")}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium">{apt.member_name}</p>
                                <p className="text-sm text-muted-foreground capitalize">
                                  {apt.visit_type.replace("_", " ")}
                                  {apt.visit_reason && ` • ${apt.visit_reason}`}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <p className="font-medium">{displayDate(apt.scheduled_date)}</p>
                                <p className="text-sm text-muted-foreground">
                                  {apt.scheduled_date ? displayTime(apt.scheduled_time, apt.scheduled_date) : apt.scheduled_time.slice(0, 5)} • {apt.duration_minutes} min
                                </p>
                              </div>
                              <Badge className={statusConfig[apt.status as keyof typeof statusConfig]?.color || ""}>
                                {statusConfig[apt.status as keyof typeof statusConfig]?.label || apt.status}
                              </Badge>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => navigate(buildProviderSessionNotesPath(apt.id))}
                              >
                                <FileText className="h-3.5 w-3.5 mr-1.5" />
                                View Notes
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </main>
      </div>

      <NewSessionDialog open={newSessionOpen} onOpenChange={setNewSessionOpen} />
      
      <ScheduleItemDetailsDialog 
        open={detailsDialogOpen}
        onOpenChange={setDetailsDialogOpen}
        appointment={selectedAppointment}
        onStartSession={(id) => {
          setDetailsDialogOpen(false);
          startSession.mutate(id);
        }}
        isStartingSession={startSession.isPending}
        onRejoinSession={(id) => {
          setDetailsDialogOpen(false);
          navigate(buildTelehealthSessionPath(id));
        }}
      />
    </div>
  );
}
